import { createSlice } from '@reduxjs/toolkit';
import api from 'src/api/index';
import * as modulesAPI from 'src/api/modules';
import * as lecturesAPI from 'src/api/lectures';
import i18n from 'src/i18n';
import { showErrorToast } from './toast';
import { updateLecture } from './lectures';
import Uploader from 'src/tools/Uploader';
import ContentCreater from 'src/tools/ContentCreater';

function generateRandomId () {
	return Date.now() + Math.floor(Math.random() * 1000000);
};

const initialState = {
	list: [],
	draft: null,
	draftModules: [],
	loading: false
};

const coursesSlice = createSlice({
	name: 'courses',
	initialState,
	reducers: {
		setCoursesList: (state, action) => {
			state.list = action.payload;
		},
		setCourseLoading: (state, action) => {
			state.loading = action.payload;
		},
		setCourseDraft: (state, action) => {
			// Создаём копию без questionAnswers вместо delete (для immutable объектов)
			const { questionAnswers, ...payloadWithoutQA } = action.payload;
			if (state.draft) {
				state.draft = { ...state.draft, ...payloadWithoutQA };
			} else {
				state.draft = payloadWithoutQA;
			}
		},
		removeCourseDraft: (state) => {
			state.draft = null;
			state.draftModules = [];
		},
		setDraftModules: (state, action) => {
			state.draftModules = action.payload;
		},
		addModuleDraft: (state, action) => {

			const id = generateRandomId();
			const orderNumber = state.draftModules.length;

			state.draftModules.push({ 
				...action.payload, 
				id, 
				orderNumber 
			});

		},
		updateModuleDraft: (state, action) => {

			const index = state.draftModules.findIndex(item => item.id === action.payload.id);

			if (index !== -1) {
				state.draftModules.splice(index, 1, action.payload);
			}

		},

		addDraftLectureOnModule: (state, action) => {

			const currentModule = state.draftModules.find(item => item.id === action.payload.module.id);
			if (currentModule) {
				currentModule.lectures.push(action.payload.lecture);
			}

		},
 		addCoursesItem: (state, action) => {
			state.list.push(action.payload);
		},
		removeCoursesItem: (state, action) => {
			state.list = state.list.filter((item) => action.payload !== item.id);
		},
		updateCourseItem: (state, action) => {
			const index = state.list.findIndex(item => item.id === action.payload.id);
			if (index !== -1) {
				state.list.splice(index, 1, action.payload);
			}
		}
	},
});

export const { setCoursesList, addCoursesItem, removeCoursesItem, updateCourseItem,
	setCourseDraft, setDraftModules, addDraftLectureOnModule, addModuleDraft,
	updateModuleDraft, setCourseLoading, removeCourseDraft } = coursesSlice.actions;

export const fetchCourseList = () => async (dispatch) => {
	try {

		const res = await api.coursesList();
		dispatch(setCoursesList(res.list));

	} catch (error) {

		throw error;
		
	}
};

export const getCourseById = (id) => ( dispatch, getState ) => {

	return new Promise(( resolve, reject ) => {

		const state = getState();

		const list = state.courses.list;

		let item = list.find(item => item.id === id);

		if (item) {
			// Обновляем draft и draftModules даже если курс уже в кеше
			dispatch(setCourseDraft(item));
			dispatch(setDraftModules(item.modules || []));
			resolve(item);

		} else {

			api.courseById(id)
				.then(res => {
					dispatch(addCoursesItem(res));
					// Также обновляем draft и draftModules для корректной работы редактирования
					dispatch(setCourseDraft(res));
					dispatch(setDraftModules(res.modules || []));
					resolve(res);
				})
				.catch(() => {

					dispatch(showErrorToast({
						description: i18n.t('toasts.courseNotFound', { id })
					}));

					reject();

				});
		}

	});

};

export const saveCourseFromDraft = ( course ) => async ( dispatch, getState ) => {

	const queue = [];

	const state = getState();
	// draftCourse обязательно должен быть копией курса, из аргументов
	// Используем модули из переданного course, если они есть, иначе из state
	const draftModules = course.modules || state.courses.draftModules;
	const draftCourse = state.courses.draft;
	
	// Получаем актуальный курс с сервера для корректного сравнения
	let originalCourse;
	try {
		originalCourse = await api.courseById(course.id);
	} catch (error) {
		// Если курс не найден на сервере (новый курс), используем переданный
		originalCourse = course;
	}

	const sendLectureToModule = async (module, lectureId) => {
		// Получаем актуальный модуль с сервера чтобы не потерять уже добавленные лекции
		const currentCourse = await api.courseById(course.id);
		const currentModule = currentCourse.modules.find(m => m.id === module.id);
		
		const module_copy = JSON.parse( JSON.stringify( module ) );
		// Берем актуальные лекции с сервера (не из локального состояния)
		module_copy.lectures = currentModule ? currentModule.lectures.map(l => l.id) : [];
		
		module_copy.lectures.push(lectureId);

		await dispatch(editCourseModule({
			courseId: course.id,
			module: module_copy
		}));

	};

	for (const module of draftModules) {

		async function updateOrCreateModule() {

			const findModule = originalCourse.modules?.find(item => item.id === module.id);

			let moduleId = module.id;

			if (module.isDraft) {
				// Создаем модуль если ранее такого модуля не было
				moduleId = await dispatch(createCourseModule( { 
					
					courseId: course.id,
					module: {
						name: module.name,
						lectures: []
					}

				}));
				
				// Если у модуля есть локальный Quiz, создаём его на сервере
				if (module.quiz && module.quiz.isDraft) {
					
					const QuizManager = (await import('src/tools/QuizManger')).default;
					const quizManager = new QuizManager();
					
					const quiz = {
						name: module.quiz.name,
						timeEstimate: module.quiz.timeEstimate,
						quizableType: 'course_modules',
						quizableId: moduleId,
						isActive: true
					};
					
					// Преобразуем questions из массива в объект (как в QuizContext)
					const questionsArray = module.quiz.questions || [];
					
					// Если нет вопросов - создаём пустой quiz
					if (questionsArray.length === 0) {
						await quizManager.createQuiz(quiz);
					} else {
						// Если есть вопросы - создаём quiz с вопросами
						const questions = {};
						const options = {};
						
						questionsArray.forEach((q, index) => {
							const questionKey = q.tempId || q.id || `question_${index}`;
							
							// Создаём копию вопроса без options (они передаются отдельно)
							const { options: questionOptions, ...questionWithoutOptions } = q;
							
							questions[questionKey] = {
								...questionWithoutOptions,
								tempId: questionKey
							};
							
							// Options хранятся отдельно по ключу вопроса
							if (questionOptions && questionOptions.length > 0) {
								options[questionKey] = questionOptions;
							}
						});
						
						await quizManager.createQuizAllData(quiz, questions, options);
					}
				}
			} else {
			// Сравниваем порядок лекций и другие поля модуля
				const newLectureIds = module.lectures.filter(item => !item.isDraft).map(item => item.id);
				const oldLectureIds = findModule ? findModule.lectures.filter(item => !item.isDraft).map(item => item.id) : [];

				// Удаляем лекции которые были заменены (есть в old, но нет в new)
				const removedLectureIds = oldLectureIds.filter(id => !newLectureIds.includes(id));
				for (const removedId of removedLectureIds) {
					try {
						await lecturesAPI.deleteLecture(removedId);
					} catch (e) {
						console.error('Failed to delete replaced lecture:', removedId, e);
					}
				}
			
				const lecturesChanged = JSON.stringify(newLectureIds) !== JSON.stringify(oldLectureIds);
				const moduleChanged = module.name !== (findModule?.name) || module.orderNumber !== (findModule?.orderNumber);
			
				if (lecturesChanged || moduleChanged) {
					// Тут проверка на соответсвие с исходником
					await dispatch(editCourseModule({

						courseId: course.id,
						module: {
							id: module.id,
							name: module.name,
							lectures: newLectureIds,
							orderNumber: module.orderNumber
						}

					}));
				}
			
				// Проверяем наличие новых вопросов в quiz существующего модуля
				if (module.quiz && module.quiz.questions && module.quiz.questions.length > 0) {
					const isDraftQuiz = Boolean(module.quiz.isDraft) || String(module.quiz.id).startsWith('draft_quiz_');
					const draftQuestions = module.quiz.questions.filter(q => q.isDraft);

					if (isDraftQuiz || draftQuestions.length > 0) {
						const QuizManager = (await import('src/tools/QuizManger')).default;
						const quizManager = new QuizManager();

						let quizId = module.quiz.id;

						// Quiz добавили в уже существующий модуль - его ещё нет на сервере
						if (isDraftQuiz) {
							console.log('📝 Creating draft quiz for existing module:', module.id, module.quiz);

							quizId = await quizManager.createQuiz({
								name: module.quiz.name,
								timeEstimate: module.quiz.timeEstimate,
								quizableType: 'course_modules',
								quizableId: module.id,
								isActive: module.quiz.isActive ?? true
							});

							console.log('✅ Draft quiz created with id:', quizId);
						}

						// У нового quiz на сервер уходят все вопросы, у существующего - только новые
						const questionsToCreate = isDraftQuiz ? module.quiz.questions : draftQuestions;

						for (const q of questionsToCreate) {
							const { options: questionOptions, id, isDraft, tempId, ...questionData } = q;

							await quizManager.createQuestion(
								quizId,
								questionData,
								questionOptions || []
							);
						}
						console.log('✅ Draft questions created for quiz:', quizId);
					}
				}
			}
			
			if (module.quiz && findModule && findModule.quiz && findModule.quiz.questions && module.quiz.questions) {
				
				// Проверяем каждый вопрос на изменение orderNumber
				for (const question of module.quiz.questions) {
					// Пропускаем draft вопросы (у них еще нет ID на сервере)
					if (question.isDraft) continue;
					
					const originalQuestion = findModule.quiz.questions.find(q => q.id === question.id);
					
					if (originalQuestion && originalQuestion.orderNumber !== question.orderNumber) {
						// Обновляем orderNumber на сервере
						try {
							const QuizManager = (await import('src/tools/QuizManger')).default;
							await QuizManager.updateQuestion({
								quizId: module.quiz.id,
								question: {
									id: question.id,
									orderNumber: question.orderNumber
								}
							});
						} catch (error) {
							console.error('Error updating question order:', error);
						}
					}
				}
			}

			// Проверяем удалённые вопросы (которые были, но их нет в локальном состоянии)
			if (module.quiz && findModule && findModule.quiz && findModule.quiz.questions && module.quiz.questions) {
				const { updateQuestion } = await import('src/api/quiz');
				
				// Проверяем каждый вопрос на изменение orderNumber
				for (const question of module.quiz.questions) {
					// Пропускаем draft вопросы (у них еще нет ID на сервере)
					if (question.isDraft) continue;
					
					const originalQuestion = findModule.quiz.questions.find(q => q.id === question.id);
					
					if (originalQuestion && originalQuestion.orderNumber !== question.orderNumber) {
						console.log('🔄 Question order changed:', question.id, 'from', originalQuestion.orderNumber, 'to', question.orderNumber);
						
						// Обновляем orderNumber на сервере
						try {
							await updateQuestion({
								quizId: module.quiz.id,
								questionId: question.id,
								payload: {
									...question,
									orderNumber: question.orderNumber
								}
							});
							console.log('✅ Updated question order:', question.id, 'to', question.orderNumber);
						} catch (error) {
							console.error('Error updating question order:', error);
						}
					}
				}
			}

			for (const lecture of module.lectures) {
				
				if (lecture.isDraft) {
					const uploader = new Uploader(lecture.file);
					const contentCreater = new ContentCreater('lecture');
					const stream = await uploader.upload();
					const contentId = await contentCreater.createContent({
						name: lecture.name,
						description: lecture.description,
						streamID: stream.ID,
					});
					await sendLectureToModule({ ...module, id: moduleId }, contentId);
				} else {
					// Тут проверка на соответсвие с исходником
					if (findModule && findModule.lectures) {
						const findLecture = findModule.lectures.find( item => item.id === lecture.id);
						if (JSON.stringify(lecture) !== JSON.stringify(findLecture)) {
							await dispatch(updateLecture(lecture));
						}
					}
				}
			}
			// Добавляем к модулю лекции, если ранее их не было
			// Удаляем attachments которые были удалены
			if (findModule && findModule.attachments) {
				const currentAttachmentIds = module.attachments.map(a => a.id);
				const removedAttachments = findModule.attachments.filter(
					oldAttachment => !currentAttachmentIds.includes(oldAttachment.id)
				);
				for (const removedAttachment of removedAttachments) {
					try {
						await modulesAPI.removeAttachment({
							courseId: course.id,
							moduleId: moduleId,
							attachmentId: removedAttachment.id
						});
					} catch (error) {
						console.error('Error removing attachment:', error);
					}
				}
			}
		
			// Загружаем новые attachments
			for (const attachment of module.attachments) {
			
				if (attachment.isDraft) {
				
					const uploader = new Uploader(attachment.file);
				
					await uploader.uploadAttachmentOnModule(course.id, moduleId);
				
				}
			
			}


			

		}

		queue.push(await updateOrCreateModule());

	}

	await Promise.all(queue);
	await dispatch(updateCourse(draftCourse));

};


export const updateCourse = (course) => async (dispatch) => {

	try {
		// Преобразуем теги в массив ID перед отправкой
		const coursePayload = {
			...course,
			tags: Array.isArray(course.tags) 
				? course.tags.map(item => typeof item === 'object' ? item.id : item)
				: []
		};

		const res = await api.updateCourse( course.id, coursePayload );

		if (res) {
			dispatch(reloadCourse(course.id));
		}

	} catch (e) {

		console.error(e);

		dispatch(showErrorToast({
			description: i18n.t('toasts.failedUpdateCourse')
		}));

		throw e;

	}

};


export const reloadCourse = (courseId) => async (dispatch) => {

	try {

		const course = await api.courseById(courseId);

		if (course) {
			dispatch(updateCourseItem(course));
		}

	} catch (error) {

		console.error(error);

		dispatch(showErrorToast({
			description: i18n.t('toasts.failedUpdateCourse')
		}));

		throw error;

	}

};

export const createCourseModule = ({courseId, module}) => async (dispatch) => {

	try {

		const moduleId = await api.createCourseModule({
			courseId,
			module
		});

		dispatch(reloadCourse(courseId));

		return moduleId;

	} catch (error) {

		console.error(error);

		dispatch(showErrorToast({
			description: i18n.t('toasts.failedCreateModule')
		}));

		throw error;

	}

};

export const editCourseModule = ({ courseId, module }) => async (dispatch) => {

	try {
		await api.editCourseModule({
			courseId,
			module
		});

		dispatch(reloadCourse(courseId));

	} catch (error) {

		console.error(error);

		dispatch(showErrorToast({
			description: i18n.t('toasts.failedEditModule')
		}));

		throw error;

	}

};

export const removeCourseModule = ({ courseId, moduleId }) => async (dispatch) => {

	try {

		await api.removeCourseModule({
			courseId,
			moduleId
		});

		dispatch(reloadCourse(courseId));

	} catch (error) {

		console.error(error);

		dispatch(showErrorToast({
			description: i18n.t('toasts.failedCreateModule')
		}));

		throw error;

	}

};

export const removeModuleAttachment = ({ courseId, moduleId, attachmentId }) => async (dispatch, getState ) => {

	try {

		await api.removeAttachmentModule({
			courseId,
			moduleId,
			attachmentId
		});

		dispatch(reloadCourse(courseId));
		

	} catch (error) {

		console.error(error);

		dispatch(showErrorToast({
			description: i18n.t('toasts.failedDeleteModuleFile')
		}));

		throw error;

	}

};

export default coursesSlice.reducer;
